import { CircularProgress, TableContainer } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { withStyles } from "@material-ui/styles";
import clsx from "clsx";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { t } from "ttag";
import { Alert as SvgAlert } from "../../icons/";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import assetsService from "../../services/assetsService";
import { systemTableStyle } from "./SystemTable.style";
import { truncateText } from "@components/TruncatedText";

interface IProps {
  onSystemDoubleClick: (systemId: string) => void;
  classes?: any;
  counts?: { indoors?: number, outdoors?: number, systems?: number, allUnits?: number, sites?: number };
  setCounts: (counts: { indoors?: number, outdoors?: number, systems?: number, allUnits?: number, sites?: number }) => void;
  isSelectedSite?: boolean;
}
const SystemTable: React.FC<IProps> = (props: IProps) => {

  const columns = [
    { id: "name", label: t`SYSTEM`, lessWidth: true },
    { id: "hasAlerts", label: t`ALERTS`, removePadding: true },
    { id: "brand", label: t`BRAND`, minWidth: true },
    { id: "type", label: t`TYPE` },
    { id: "allUnits", label: t`ODU/IDU` },
    { id: "capacity", label: t`CAPACITY` }
  ];
  const columnsSites = [
    { id: "siteName", label: t`SITE` },
    { id: "systems", label: t`SYSTEMS` },
    { id: "outdoor", label: t`ODU` },
    { id: "indoor", label: t`IDU` }
  ];

  const [rows, setRows] = useState<any>([]);

  const { classes, counts, setCounts, isSelectedSite } = props;
  const { alertIcon, tableWrapper, tableCell, tableRow, brandLogo } = classes;
  const setSelections = useStoreActions((s) => s.selections.setSelections);
  const capacityMeasurementUnitTypes = _.invertBy(useStoreState((state) => state.types.capacityMeasurementUnitTypes));
  const sitesFlags = useStoreState((s) => s.sites.sitesFlags);
  const unitTypes = useStoreState((s) => s.types.unitTypes);
  const getSiteName = useStoreState((state) => state.sites.getSiteName);
  const getSiteSystemsStats = useStoreActions((actions) => actions.sites.getSiteSystemsStats);
  const getCustomerSystemsStats = useStoreActions((actions) => actions.customers.getCustomerSystemsStats);
  const selections = useStoreState((state) => state.selections.selections);
  const { customerId, siteId } = selections;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!siteId && !customerId) {
        return;
      }
      setIsLoading(true);
      try {
        setRows([]); // Reset current rows
        let res ;
        if(siteId) {
          res = await getSiteSystemsStats(siteId);
        } else if(customerId) {
          res = await getCustomerSystemsStats(customerId);
        } else {
          return;
        }
        let totalIndoor = 0;
        let totalOutdoor = 0;
        let totalSystems = 0;
        let tempRows: any[] = [];

        if (isSelectedSite) {
          tempRows = res.map(
            ({
              name,
              system,
              brandNum,
              unitCounts,
              isAlert,
              capacityMeasurementUnits,
              capacity,
              type,
            }: any) => {
              const { indoor, outdoor } = unitCounts.reduce(
                (
                  acc: { indoor: number; outdoor: number },
                  { type, count }: any
                ) => {
                  if (type === unitTypes.indoor) acc.indoor += count;
                  if (type === unitTypes.outdoor) acc.outdoor += count;
                  return acc;
                },
                { indoor: 0, outdoor: 0 }
              );

              totalIndoor += indoor;
              totalOutdoor += outdoor;

              return {
                id: system,
                name,
                siteName: getSiteName(siteId || ''),
                siteId,
                type,
                indoor,
                outdoor,
                brand: brandNum,
                hasAlerts: isAlert,
                capacityMeasurementUnits:
                  capacityMeasurementUnitTypes[capacityMeasurementUnits] || '',
                capacity: capacity || 0,
              };
            }
          );
        } else {
          res.forEach(({ systemCount, unitCounts }: any) => {
            const { indoor, outdoor } = unitCounts.reduce(
              (
                acc: { indoor: number; outdoor: number },
                { type, count }: any
              ) => {
                if (type === unitTypes.indoor) acc.indoor += count;
                if (type === unitTypes.outdoor) acc.outdoor += count;
                return acc;
              },
              { indoor: 0, outdoor: 0 }
            );

            totalIndoor += indoor;
            totalOutdoor += outdoor;
            totalSystems += systemCount;
          });
        }

        setRows(isSelectedSite ? tempRows : res);
        setCounts({
          ...counts,
          outdoors: totalOutdoor,
          indoors: totalIndoor,
          systems: isSelectedSite ? tempRows.length : totalSystems,
          sites: isSelectedSite ? 1 : res.length,
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [siteId, customerId]);

  const getRows = () => {
    const tempRowsObj: any = {};
    if (isSelectedSite){
      return rows.sort((a: any, b: any) => a.name?.localeCompare(b.name));
    }

    rows.forEach((row: any) => {
      const { site, siteName, systemCount, unitCounts } = row;
      if (sitesFlags[site] && !sitesFlags[site]?.hasDashboard) {
        return;
      }
      if (!tempRowsObj[site]){
        tempRowsObj[site] = {} as any;
      }
      let tempIndoor = 0;
      let tempOutdoor = 0;
      unitCounts?.forEach(({ type, count }: any) => {
        if (type === unitTypes.indoor) {
          tempIndoor += count;
        }
        if (type === unitTypes.outdoor) {
          tempOutdoor += count;
        }
      });
      tempRowsObj[site].siteName = siteName;
      tempRowsObj[site].siteId = site;
      tempRowsObj[site].id = site;
      tempRowsObj[site].systems = systemCount;
      tempRowsObj[site].indoor = tempIndoor;
      tempRowsObj[site].outdoor = tempOutdoor;
    });

    return Object.values(tempRowsObj).sort((a: any, b: any) => a.siteName?.localeCompare(b.siteName));
  };

  const getColumns = () => isSelectedSite ? columns : columnsSites;

  columns.forEach((col) => {
    if (col.id === "allUnits")
      col.label = t`ODU` + `(${counts?.outdoors})` + ` / ` + t`IDU` + `(${counts?.indoors})`;
  });

  if (isLoading) {
    return (
      <div className={classes.loaderHolder}>
        <CircularProgress />
      </div>
    );
  }
  
  return (
    <div className={classes.pageContainer}>
      <TableContainer className={tableWrapper}>
        <Table stickyHeader aria-label="a dense table">
          <TableHead>
            <TableRow>
              {getColumns().map((column: any, index) => (
                <TableCell
                  key={index}
                  align={(column.id === "name" || column.id === "siteName") ? "left" : "center"}
                  classes={{ root: classes.tableHeadCell }}
                >
                  {column.id === "hasAlerts" ? "" : column.label}
                  {column.id === "name" ? ` (${rows.length})` : ""}
                  {column.id === "outdoor" ? ` (${counts?.outdoors})` : ""}
                  {column.id === "indoor" ? ` (${counts?.indoors})` : ""}
                  {column.id === "systems" ? ` (${counts?.systems || 0})` : ""}
                  {column.id === "siteName" ? ` (${counts?.sites || 0})` : ""}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {getRows().map((row: any) => {
              return (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row.id}
                  onDoubleClick={() => {
                    if (!isSelectedSite)
                      setSelections({ siteId: row.siteId });
                    else
                      props.onSystemDoubleClick(row.id);
                  }}
                  className={tableRow}
                >
                  {getColumns().map((column: any, index) => {
                    const rowVal = row[column.id],
                      value =
                        column.id === "brand" ? (
                          <img
                            width={60}
                            height={25}
                            src={assetsService.getBrandLogo(`${rowVal}-grey.svg`)}
                            onError={(event: any) =>
                              (event.target.src = assetsService.getBrandLogo("empty.png"))
                            }
                            alt={rowVal}
                            className={brandLogo}
                          />
                        )
                          : column.id === "hasAlerts" && row.hasAlerts ? (<SvgAlert className={alertIcon} />)
                            : column.id === "allUnits" ? `${row.outdoor} / ${row.indoor}`
                              : column.id === "capacity" ? `${rowVal ? (rowVal + ' ' + row.capacityMeasurementUnits) : ('')}`
                                : rowVal;

                    return (
                      <TableCell
                        size="small"
                        key={index}
                        className={clsx(tableCell, { [classes.minWidthStyle]: column.minWidth, [classes.lessWidth]: column.lessWidth, [classes.removePadding]: column.removePadding })}
                        align={column.id === "name" || column.id === "siteName" ? "left" : "center"}
                      >
                        {column.format && typeof value === "number" ? column.format(value) :
                          (column.minWidth && value && value.length > 30 ? value.substr(0, 30) + "\u2026"
                            : truncateText(value, 20))}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
export default withStyles(systemTableStyle)(SystemTable);
